export const PLATFORM_OPTIONS = [
  { value: 'SHEIN', label: 'SHEIN' },
  { value: 'TEMU', label: 'TEMU' },
  { value: 'TIKTOK', label: 'TIKTOK' },
  { value: 'AMAZON', label: 'Amazon' },
];

/**
 * 平台品牌标识（内部真相源）。
 * 做哪个平台，就以这里的 color / accent 作为该平台标牌与主操作色；
 * 店铺管理 Tab、列表平台文字、工作区主按钮应对齐同一套色值。
 *
 * SHEIN 黑标：浅底用 color；黑底/深色底用 colorOnDark（白）以保证可读。
 *
 * @typedef {{
 *   code: string,
 *   label: string,
 *   color: string,
 *   colorOnDark?: string,
 *   accent: string,
 *   onAccent: string,
 *   soft: string,
 *   softInk: string,
 *   secondary?: string,
 * }} PlatformBrand
 */
export const PLATFORM_BRAND = Object.freeze({
  SHEIN: Object.freeze({
    code: 'SHEIN',
    label: 'SHEIN',
    /** 黑标牌（浅色底） */
    color: '#111827',
    /** 黑标在深色底上的可读色 */
    colorOnDark: '#ffffff',
    accent: '#111827',
    onAccent: '#ffffff',
    soft: '#f3f4f6',
    softInk: '#111827',
  }),
  TEMU: Object.freeze({
    code: 'TEMU',
    label: 'TEMU',
    /** 橙标牌 */
    color: '#ff6a00',
    accent: '#ff6a00',
    onAccent: '#0a0a0a',
    soft: '#fff7ed',
    softInk: '#c2410c',
  }),
  TIKTOK: Object.freeze({
    code: 'TIKTOK',
    label: 'TIKTOK',
    /** 青标牌（TikTok 专属） */
    color: '#25f4ee',
    accent: '#25f4ee',
    onAccent: '#0a0a0a',
    soft: '#ecfeff',
    softInk: '#0f766e',
    secondary: '#fe2c55',
  }),
  AMAZON: Object.freeze({
    code: 'AMAZON',
    label: 'Amazon',
    color: '#ff9900',
    accent: '#ff9900',
    onAccent: '#0a0a0a',
    soft: '#fffbeb',
    softInk: '#92400e',
  }),
});

/** @deprecated 优先用 PLATFORM_BRAND / resolvePlatformBrand；保留别名键兼容旧引用 */
export const PLATFORM_COLOR_MAP = {
  TEMU: PLATFORM_BRAND.TEMU.color,
  'TEMU半托': PLATFORM_BRAND.TEMU.color,
  SHEIN: PLATFORM_BRAND.SHEIN.color,
  Shein: PLATFORM_BRAND.SHEIN.color,
  'SHEIN半托': PLATFORM_BRAND.SHEIN.color,
  TikTok: PLATFORM_BRAND.TIKTOK.color,
  TIKTOK: PLATFORM_BRAND.TIKTOK.color,
  AMAZON: PLATFORM_BRAND.AMAZON.color,
  亚马逊: PLATFORM_BRAND.AMAZON.color,
  '店小秘': '#000000',
};

/** SHEIN 开放平台三种合作模式 */
export const SHEIN_SHOP_TYPE_OPTIONS = ['半托管', '自运营', '全托管'];

/** TEMU 开放平台三轨类型（「本本」为历史名，规范为「本土」） */
export const TEMU_SHOP_TYPE_OPTIONS = ['半托管', '全托管', '本土'];

/** TikTok 店铺类型。全托管是 Global Selling 独立应用，接口 seller_type 不会回写这一档。 */
export const TIKTOK_SHOP_TYPE_OPTIONS = ['跨境', '本土', '全托管'];

/**
 * TikTok 国家/市场：授权后由平台 region 回写；列表展示用。
 * 店铺类型同样由授权店铺 seller_type 回写；本土可选国家见 TIKTOK_LOCAL_COUNTRY_CODES。
 */
export const TIKTOK_COUNTRY_OPTIONS = [
  { value: 'US', label: '美国' },
  { value: 'MX', label: '墨西哥' },
  { value: 'GB', label: '英国' },
  { value: 'DE', label: '德国' },
  { value: 'FR', label: '法国' },
  { value: 'IT', label: '意大利' },
  { value: 'ES', label: '西班牙' },
  { value: 'IE', label: '爱尔兰' },
  { value: 'PL', label: '波兰' },
  { value: 'NL', label: '荷兰' },
  { value: 'BE', label: '比利时' },
  { value: 'ID', label: '印度尼西亚' },
  { value: 'MY', label: '马来西亚' },
  { value: 'TH', label: '泰国' },
  { value: 'VN', label: '越南' },
  { value: 'PH', label: '菲律宾' },
  { value: 'SG', label: '新加坡' },
  { value: 'JP', label: '日本' },
  { value: 'BR', label: '巴西' },
];

/**
 * 本土店可选国家（顺序与产品一致）：
 * 越南、美国、泰国、新加坡、菲律宾、马来西亚、墨西哥、日本、印度尼西亚、巴西。
 */
const TIKTOK_LOCAL_COUNTRY_CODES = ['VN', 'US', 'TH', 'SG', 'PH', 'MY', 'MX', 'JP', 'ID', 'BR'];
const TIKTOK_LOCAL_COUNTRY_SET = new Set(TIKTOK_LOCAL_COUNTRY_CODES);
const TIKTOK_COUNTRY_LABELS = Object.fromEntries(
  TIKTOK_COUNTRY_OPTIONS.map((item) => [item.value, item.label]),
);

export function normalizeTiktokCountryCode(country) {
  const raw = String(country || '').trim().toUpperCase();
  if (!raw) return '';
  const code = raw === 'UK' ? 'GB' : raw;
  if (TIKTOK_COUNTRY_LABELS[code]) return code;
  if (/^[A-Z]{2}$/.test(code)) return code;
  return '';
}

/** 未知国家时允许先选本土，授权回写后再校验；白名单内国家可选本土 */
export function tiktokAllowsLocalShopType(country) {
  const code = normalizeTiktokCountryCode(country);
  if (!code) return true;
  return TIKTOK_LOCAL_COUNTRY_SET.has(code);
}

export function tiktokCountryLabel(country) {
  const code = normalizeTiktokCountryCode(country);
  if (!code) return String(country || '').trim() || '-';
  return TIKTOK_COUNTRY_LABELS[code] || code;
}

export function tiktokLocalUnavailableMessage(country) {
  const label = tiktokCountryLabel(country);
  return `当前店铺国家（${label}）没有本土类型`;
}

/**
 * 新建 TikTok 店可选国家：本土为白名单 10 国；跨境全量；全托管不选国家（返回空）。
 */
export function tiktokCountryOptionsForShopType(shopType) {
  const type = normalizeTiktokShopType(shopType) || String(shopType || '').trim();
  if (type === '全托管') return [];
  if (type === '本土') {
    const byCode = Object.fromEntries(TIKTOK_COUNTRY_OPTIONS.map((item) => [item.value, item]));
    return TIKTOK_LOCAL_COUNTRY_CODES.map((code) => byCode[code]).filter(Boolean);
  }
  return [...TIKTOK_COUNTRY_OPTIONS];
}

/** 国家码 → 授权市场：美国走 .us，其它走 ROW。 */
export function tiktokAuthMarketFromCountry(country) {
  return normalizeTiktokCountryCode(country) === 'US' ? 'US' : 'ROW';
}

export {
  TEMU_AUTH_REQUIRED_API_HINT,
  TEMU_TOKEN_SLOTS,
  emptyTemuTokens,
  temuAuthStatusColumnModel,
  temuTokenFilledSummary,
  temuTokenSlotAuthStates,
  temuTokenSlotAuthUrl,
  temuTokenSlotLabel,
  temuTokenSlotsForShopType,
} from '@/utils/temuAuthSlots';

/** 非 SHEIN/TEMU/TikTok 平台通用店铺类型 */
export const DEFAULT_SHOP_TYPE_OPTIONS = ['半托', '全托', 'ODM', 'FBA', 'POP', 'OMS'];

/**
 * 全量店铺类型（含 SHEIN 新枚举与历史值），供筛选等场景。
 * @deprecated 优先使用 shopTypeOptionsForPlatform
 */
export const SHOP_TYPE_OPTIONS = [
  ...SHEIN_SHOP_TYPE_OPTIONS,
  ...TEMU_SHOP_TYPE_OPTIONS,
  ...TIKTOK_SHOP_TYPE_OPTIONS,
  ...DEFAULT_SHOP_TYPE_OPTIONS,
];

const PLATFORM_LABELS = {
  SHEIN: 'SHEIN',
  TEMU: 'TEMU',
  TIKTOK: 'TIKTOK',
  AMAZON: 'Amazon',
};

const SHEIN_SHOP_TYPE_ALIASES = {
  半托: '半托管',
  半托管: '半托管',
  '半托管店': '半托管',
  SEMI: '半托管',
  SEMI_HOSTED: '半托管',
  全托: '全托管',
  全托管: '全托管',
  '全托管店': '全托管',
  OBM: '全托管',
  FULL: '全托管',
  FULL_HOSTED: '全托管',
  自运营: '自运营',
  '自主运营': '自运营',
  SELF: '自运营',
};

export function normalizePlatformCode(platformCode) {
  const raw = String(platformCode || '').trim();
  if (!raw) return '';
  const upper = raw.toUpperCase();
  if (PLATFORM_LABELS[upper]) return upper;
  const lower = raw.toLowerCase();
  if (lower === 'amazon' || raw === '亚马逊') return 'AMAZON';
  if (lower === 'shein') return 'SHEIN';
  if (lower === 'temu') return 'TEMU';
  if (lower === 'tiktok') return 'TIKTOK';
  return upper;
}

export function platformLabel(platformCode) {
  const normalized = normalizePlatformCode(platformCode);
  if (!normalized) return '-';
  return PLATFORM_LABELS[normalized] || platformCode;
}

/** 解析平台品牌色（店铺管理 Tab / 列表文字）；未知平台返回空字符串 */
export function resolvePlatformColor(platformCode) {
  return resolvePlatformBrandColor(platformCode);
}

/**
 * 解析平台品牌展示色。
 * @param {string} platformCode
 * @param {{ onDark?: boolean }} [options] onDark=true 时：SHEIN 黑标改用白色
 */
export function resolvePlatformBrandColor(platformCode, options = {}) {
  const brand = resolvePlatformBrand(platformCode);
  if (brand) {
    if (options.onDark && brand.colorOnDark) return brand.colorOnDark;
    return brand.color;
  }
  const raw = String(platformCode || '').trim();
  if (!raw) return '';
  return PLATFORM_COLOR_MAP[raw] || PLATFORM_COLOR_MAP[raw.toUpperCase()] || '';
}

/** 解析完整平台品牌标识；未知平台返回 null */
export function resolvePlatformBrand(platformCode) {
  const normalized = normalizePlatformCode(platformCode);
  if (normalized && PLATFORM_BRAND[normalized]) {
    return PLATFORM_BRAND[normalized];
  }
  const raw = String(platformCode || '').trim();
  if (!raw) return null;
  // 历史别名：SHEIN半托 / TEMU半托 / TikTok 等
  const aliasColor = PLATFORM_COLOR_MAP[raw] || PLATFORM_COLOR_MAP[raw.toUpperCase()];
  if (!aliasColor) return null;
  const byColor = Object.values(PLATFORM_BRAND).find((b) => b.color === aliasColor);
  return byColor || null;
}

export function shopTypeOptionsForPlatform(platformCode) {
  const code = normalizePlatformCode(platformCode);
  if (code === 'SHEIN') {
    return [...SHEIN_SHOP_TYPE_OPTIONS];
  }
  if (code === 'TEMU') {
    return [...TEMU_SHOP_TYPE_OPTIONS];
  }
  if (code === 'TIKTOK') {
    return [...TIKTOK_SHOP_TYPE_OPTIONS];
  }
  return [...DEFAULT_SHOP_TYPE_OPTIONS];
}

export function defaultShopTypeForPlatform(platformCode) {
  const options = shopTypeOptionsForPlatform(platformCode);
  return options[0] || '';
}

/** TEMU 店铺类型规范化（含历史「本本」→「本土」） */
export function normalizeTemuShopType(shopType) {
  const raw = String(shopType || '').trim();
  if (!raw) return '';
  if (raw === '本本' || raw === '本本店') return '本土';
  if (TEMU_SHOP_TYPE_OPTIONS.includes(raw)) return raw;
  const upper = raw.toUpperCase();
  if (upper === 'BEN_BEN' || upper === 'LOCAL' || upper === 'L2L' || upper === 'LOCAL_TO_LOCAL' || upper === 'BEN_TU') {
    return '本土';
  }
  if (raw.includes('本本') || raw.includes('本土')) return '本土';
  if (raw.includes('半托')) return '半托管';
  if (raw.includes('全托')) return '全托管';
  return raw;
}

/** TikTok 店铺类型规范化（跨境 / 本土 / 全托管） */
export function normalizeTiktokShopType(shopType) {
  const raw = String(shopType || '').trim();
  if (!raw) return '';
  if (TIKTOK_SHOP_TYPE_OPTIONS.includes(raw)) return raw;
  const upper = raw.toUpperCase();
  const lower = raw.toLowerCase();
  if (
    raw.includes('全托')
    || upper === 'FULL'
    || upper === 'FULL_SERVICE'
    || upper === 'FULL-SERVICE'
    || upper === 'GS'
    || upper === 'GSV2'
    || lower === 'full'
  ) {
    return '全托管';
  }
  if (
    upper === 'CROSSBORDER'
    || upper === 'CROSS_BORDER'
    || upper === 'CROSS-BORDER'
    || upper === 'CB'
    || upper === 'GLOBAL'
    || lower === 'crossborder'
    || lower === 'cross-border'
    || lower === 'cb'
  ) {
    return '跨境';
  }
  if (
    upper === 'LOCAL'
    || upper === 'L2L'
    || upper === 'LOCAL_TO_LOCAL'
    || lower === 'local'
    || lower === 'l2l'
  ) {
    return '本土';
  }
  if (raw.includes('跨境') || raw.includes('跨店')) return '跨境';
  if (raw.includes('本土') || raw.includes('本本')) return '本土';
  return raw;
}

/** 将历史「半托/全托」等别名规范为 SHEIN 三种类型；非 SHEIN 别名原样返回。 */
export function normalizeSheinShopType(shopType) {
  const raw = String(shopType || '').trim();
  if (!raw) return '';
  if (SHEIN_SHOP_TYPE_OPTIONS.includes(raw)) return raw;
  const alias = SHEIN_SHOP_TYPE_ALIASES[raw] || SHEIN_SHOP_TYPE_ALIASES[raw.toUpperCase()];
  if (alias) return alias;
  if (raw.includes('半托')) return '半托管';
  if (raw.includes('全托')) return '全托管';
  if (raw.includes('自运营') || raw.includes('自主运营')) return '自运营';
  return raw;
}

export function shopTypeLabel(shopType, platformCode) {
  const value = String(shopType || '').trim();
  if (!value) return '-';
  const code = normalizePlatformCode(platformCode);
  if (code === 'SHEIN' || SHEIN_SHOP_TYPE_OPTIONS.includes(value) || SHEIN_SHOP_TYPE_ALIASES[value]) {
    return normalizeSheinShopType(value) || value;
  }
  if (code === 'TEMU') {
    return normalizeTemuShopType(value) || value;
  }
  if (code === 'TIKTOK') {
    return normalizeTiktokShopType(value) || value;
  }
  return value;
}

/** 判断店铺类型是否与目标类型一致（兼容半托↔半托管 等历史值） */
export function shopTypeEquals(shopType, targetType, platformCode = 'SHEIN') {
  const target = String(targetType || '').trim();
  if (!target) return true;
  const code = normalizePlatformCode(platformCode);
  if (code === 'SHEIN' || code === 'TEMU' || code === 'TIKTOK') {
    if (code === 'TEMU') {
      return normalizeTemuShopType(shopType) === normalizeTemuShopType(target);
    }
    if (code === 'TIKTOK') {
      return normalizeTiktokShopType(shopType) === normalizeTiktokShopType(target);
    }
    const normalizedShop = normalizeSheinShopType(shopType);
    const normalizedTarget = normalizeSheinShopType(target);
    // 未填写类型的历史 SHEIN 店铺，按半托管匹配，避免登录页选不出来
    if (!normalizedShop && code === 'SHEIN') return normalizedTarget === '半托管';
    return normalizedShop === normalizedTarget;
  }
  return String(shopType || '').trim() === target;
}
